import { readMeta } from './read-meta.js';
import { getObjectShape, getDiscriminatedBranches, getDiscriminator, isDiscriminatedUnion, isObject, } from './walk-schema.js';
/**
 * Walk a Zod object tree and collect dotted paths that are flagged
 * `.meta({ secret: true })`.
 */
export function collectSecretPaths(schema, prefix = '') {
    const out = [];
    visit(schema, prefix, out);
    return out;
}
function visit(schema, prefix, out) {
    if (!schema)
        return;
    const meta = readMeta(schema);
    if (meta.secret === true) {
        out.push(prefix);
    }
    if (isObject(schema)) {
        const shape = getObjectShape(schema);
        for (const key of Object.keys(shape)) {
            const child = shape[key];
            if (!child)
                continue;
            const childPath = prefix ? `${prefix}.${key}` : key;
            visit(child, childPath, out);
        }
        return;
    }
    if (isDiscriminatedUnion(schema)) {
        const branches = getDiscriminatedBranches(schema);
        const disc = getDiscriminator(schema);
        for (const branch of branches) {
            if (isObject(branch)) {
                const shape = getObjectShape(branch);
                for (const key of Object.keys(shape)) {
                    if (key === disc)
                        continue;
                    const child = shape[key];
                    if (!child)
                        continue;
                    const childPath = prefix ? `${prefix}.${key}` : key;
                    visit(child, childPath, out);
                }
            }
        }
        return;
    }
    // Other schema kinds (array, primitive) — skip; secrets only meaningful on object leaves.
}
function getAtPath(obj, path) {
    const parts = path.split('.').filter(Boolean);
    let cur = obj;
    for (const p of parts) {
        if (cur === null || cur === undefined || typeof cur !== 'object')
            return undefined;
        cur = cur[p];
    }
    return cur;
}
function deleteAtPath(obj, path) {
    const parts = path.split('.').filter(Boolean);
    if (parts.length === 0)
        return;
    let cur = obj;
    for (let i = 0; i < parts.length - 1; i++) {
        const p = parts[i];
        if (cur === null || cur === undefined || typeof cur !== 'object')
            return;
        cur = cur[p];
    }
    if (cur && typeof cur === 'object') {
        delete cur[parts[parts.length - 1]];
    }
}
/**
 * Given form values + RHF dirtyFields, remove any secret paths that are not dirty.
 * Returns a new object (shallow clone — callers may mutate).
 */
export function pruneUntouchedSecrets(values, dirtyFields, schema) {
    const clone = structuredCloneSafe(values);
    const paths = collectSecretPaths(schema);
    for (const path of paths) {
        const dirty = getAtPath(dirtyFields, path);
        if (!dirty) {
            const current = getAtPath(clone, path);
            if (current === '' || current === undefined || current === null) {
                deleteAtPath(clone, path);
            }
            else if (!dirty) {
                // Not dirty — user did not touch it. Drop regardless of current value
                // (may be the rehydrated placeholder).
                deleteAtPath(clone, path);
            }
        }
    }
    return clone;
}
function structuredCloneSafe(v) {
    if (typeof structuredClone === 'function') {
        try {
            return structuredClone(v);
        }
        catch {
            /* fall through */
        }
    }
    return JSON.parse(JSON.stringify(v));
}
//# sourceMappingURL=prune-untouched-secrets.js.map